// Phase 7 — Trusted Partner brand data.
// Each entry powers `/brands/:slug` (detail) and `/brands` (index).
// Image references are SLUGS resolved by <Picture> via imageManifest.json.

/**
 * Category groups used by the /brands index for visual grouping.
 * Ordered intentionally — Control first (the everyday interface),
 * then Lighting, Audio, Display & Cinema, and Networking.
 */
export const BRAND_GROUPS = [
    { id: "control", label: "Control" },
    { id: "lighting", label: "Lighting" },
    { id: "audio", label: "Audio" },
    { id: "display", label: "Display & Cinema" },
    { id: "networking", label: "Networking" },
];

export const BRANDS = [
    {
        slug: "control4",
        name: "Control4",
        tagline: "The intelligent backbone of the connected residence.",
        specialty: "Whole-home control, scenes, and AV switching",
        group: "control",
        hero: "brand_control4_hero",
        intro: [
            "Control4 is the operating layer beneath the daily life of a connected residence. Its processors compose lighting, climate, audio, video, and security into one tactile language — a single gesture composing an entire room.",
            "We specify Control4 when the project calls for a deep, dealer-supported ecosystem with a mature firmware lineage. From entry-level dressing rooms to twenty-thousand-square-foot estates, Control4's reliability and depth of integration earn it our most frequent recommendation.",
        ],
        products: [
            {
                name: "EA-5 Entertainment Controller",
                category: "Flagship Whole-Home Controller",
                icon: "Cpu",
                image: "product_control4_ea5",
                description:
                    "The processor at the heart of a Control4 residence. Coordinates AV switching, lighting recall, and scene logic with effortless responsiveness.",
                why: "The EA-5 anchors every wing of the residence — switching ten zones of audio while composing a six-room cinema scene without a perceptible pause.",
            },
            {
                name: "Halo Touch Remote",
                category: "Handheld Control",
                icon: "Watch",
                image: "product_control4_halo",
                description:
                    "A solid-metal, color-touchscreen remote that lives between sofa cushions and feels like a piece of jewelry. The everyday interface for media-room rituals.",
                why: "The Halo is the remote clients reach for instinctively — the object that earns its place on the coffee table rather than being hidden in a drawer.",
            },
            {
                name: "T4 Series Tabletop Touch Screen",
                category: "8\" / 10\" Tabletop Interface",
                icon: "PanelTop",
                image: "product_control4_t4",
                description:
                    "A discreet tabletop touchscreen for the kitchen island or bedside table — every system, one swipe away.",
                why: "Specified at every level of the residence — from the bedside button that dims the lights to the cinema 'Begin' macro that composes six rooms.",
            },
            {
                name: "Pakedge Networking",
                category: "Residential Network Infrastructure",
                icon: "Network",
                image: "product_control4_pakedge",
                description:
                    "Purpose-built routers, switches, and wireless under the same brand umbrella — engineered for the demands of a smart residence.",
                why: "A single, vetted vendor from controller to network means one support call, one firmware cadence, and one team that knows the whole stack.",
            },
        ],
        benefits: [
            "A single, vetted ecosystem from controller to network — fewer vendor seams, fewer support handoffs.",
            "Granular scene logic (\u201CGoodnight,\u201D \u201CArrival,\u201D \u201CCinema\u201D) composable across lighting, audio, climate, and security.",
            "Mature dealer support with rapid firmware stewardship — what we install today is supported tomorrow.",
        ],
        pairsWith: ["home-automation", "home-theater-audio"],
    },
    {
        slug: "savant",
        name: "Savant",
        tagline: "Cinematic interfaces for the discerning residence.",
        specialty: "Premium handheld control, smart lighting, and elegant UI",
        group: "control",
        hero: "brand_savant_hero",
        intro: [
            "Savant is the platform we specify when the principal cares about the texture of the interface as much as the engineering beneath it. Its handheld remotes feel like watchmaking, and its iOS app is the only one in the category that an unbriefed family member actually wants to open.",
            "We deploy Savant in residences where the Apple aesthetic is already established — and where a beautifully composed control layer is part of the architectural story, not a tolerated necessity.",
        ],
        products: [
            {
                name: "Pro Remote X2",
                category: "Aluminum Handheld with OLED",
                icon: "Watch",
                image: "product_savant_proremote",
                description:
                    "A precisely-machined remote with a backlit OLED display — control your home with the tactile pleasure of a high-end watch.",
                why: "Heavy in the hand in the way good watches are. Clients who handle one don't ask for an alternative.",
            },
            {
                name: "SmartAmp",
                category: "Distributed Audio Amplifier",
                icon: "Speaker",
                image: "product_savant_smartamp",
                description:
                    "A reference distributed-audio amplifier engineered to drive architectural in-wall and in-ceiling speakers throughout the residence. Powerful, silent, and rack-mountable — the foundation of the Savant audio architecture.",
                why: "Pairs natively with the Savant Pro App for zone-level control. The amplification layer disappears into the equipment closet while the music fills every room.",
            },
            {
                name: "Smart Lighting with TrueImage",
                category: "Color-Tuning Keypads",
                icon: "Lightbulb",
                image: "product_savant_lighting",
                description:
                    "Engraved keypads paired with full-spectrum tunable lighting — daylight in the morning, candlelight in the evening, automatically.",
                why: "The system disappears into the rhythm of the day — the room simply gets warmer toward dinner without anyone touching a thing.",
            },
            {
                name: "Savant Pro App",
                category: "iOS / iPadOS Surface",
                icon: "Smartphone",
                image: "product_savant_proapp",
                description:
                    "The most elegant control app in the category — a system that feels like Apple's own product, on Apple's own devices.",
                why: "We routinely watch principals adopt the Savant app within a single afternoon. Adoption is the only metric of a control system that matters.",
            },
        ],
        benefits: [
            "The most beautiful out-of-box UI of any major control platform — clients use it more, which is the only metric that matters.",
            "Tight Apple-platform integration: HomeKit, Apple TV, and AirPlay handled natively.",
            "Premium handheld hardware that complements the architecture, not detracts from it.",
        ],
        pairsWith: ["home-automation", "lighting-shades", "home-theater-audio"],
    },
    {
        slug: "lutron",
        name: "Lutron",
        tagline: "The art of architectural light, perfected.",
        specialty: "Lighting control and motorized shading",
        group: "lighting",
        hero: "brand_lutron_hero",
        intro: [
            "Lutron has been the unrivalled standard in residential lighting control for a generation, and we have yet to find a project at our tier where it isn't the right answer. The keypads are architectural ornament; the shades are silent; the engineering is the benchmark against which everything else is measured.",
            "Where Lutron earns its place beyond reliability is in the atmospheric upgrade Ketra brings — tunable, full-spectrum light that follows the sun and renders interior finishes the way the architect intended.",
        ],
        products: [
            {
                name: "HomeWorks RA3",
                category: "Whole-Home Lighting Processor",
                icon: "Lightbulb",
                image: "product_lutron_ra3",
                description:
                    "Lutron's flagship residential lighting control system — recalled in nine out of ten projects at our tier.",
                why: "The lighting platform that simply works — twenty years from now, today's RA3 installation will still be supported and current.",
            },
            {
                name: "Palladiom Keypads",
                category: "Slim Metal Engraved Controls",
                icon: "Lightbulb",
                image: "product_lutron_palladiom",
                description:
                    "A keypad designed as architectural hardware first — available in over 25 metal finishes, engraved per scene.",
                why: "Specified by the interior designer rather than the integrator — Palladiom keypads belong to the room before they belong to the system.",
            },
            {
                name: "Sivoia QS Triathlon Shades",
                category: "Silent Battery-Free Shading",
                icon: "Sun",
                image: "product_lutron_sivoia",
                description:
                    "Hush-quiet motorized shades with a five-year battery and Lutron's renowned reliability.",
                why: "The shading you do not hear — and the only motorized shade we trust in a master suite without a backup plan.",
            },
            {
                name: "Ketra Natural Light",
                category: "Circadian Tunable Fixtures",
                icon: "SunMedium",
                image: "product_lutron_ketra",
                description:
                    "Tunable-white fixtures that shift color and intensity through the day to mirror the natural progression of sunlight.",
                why: "The single greatest atmospheric upgrade we can deliver — daylight at the breakfast table, candlelight by dinner.",
            },
        ],
        benefits: [
            "The single most reliable lighting platform on the market — the standard against which others are measured.",
            "Keypads serve as both control and architectural ornament — engraved scene labels in matte brass become part of the room.",
            "Tunable lighting (Ketra) is the single greatest atmospheric upgrade we can deliver in a residence.",
        ],
        pairsWith: ["lighting-shades", "home-automation"],
    },
    {
        slug: "sonos",
        name: "Sonos",
        tagline: "Effortless music, everywhere it should be.",
        specialty: "Distributed audio and casual listening",
        group: "audio",
        hero: "brand_sonos_hero",
        intro: [
            "Sonos is the platform we specify for the everyday soundtrack of the residence — distributed across the kitchen, the garden, the terrace, and the staff quarters. It is the audio infrastructure that staff and family operate without instruction; the platform that simply works.",
            "We pair Sonos with architectural in-wall and in-ceiling speakers so the music fills the room rather than the speaker grilles being visible from the dining table. The result is presence without intrusion.",
        ],
        products: [
            {
                name: "Era 300",
                category: "Spatial-Audio Speaker",
                icon: "Speaker",
                image: "product_sonos_era300",
                description:
                    "Dolby Atmos in a single elegant form — surprising depth from a deliberately compact shape.",
                why: "A statement speaker that does not announce itself — disappears into a shelf and fills a room with spatially-mixed audio.",
            },
            {
                name: "Arc Ultra",
                category: "Flagship Soundbar",
                icon: "Speaker",
                image: "product_sonos_arcultra",
                description:
                    "Atmos soundbar with the new Sound Motion driver — cinema-grade bass without the cinema-grade footprint.",
                why: "The right answer for the family room television — Atmos without a separate subwoofer, sub-architectural in profile.",
            },
            {
                name: "Sub 4",
                category: "Wireless Subwoofer",
                icon: "Speaker",
                image: "product_sonos_sub4",
                description:
                    "Discreet, wireless, and architecturally placeable — adds depth without dictating placement.",
                why: "Specified where Atmos depth matters but a visible subwoofer would compromise the room. Slides behind the sofa, disappears.",
            },
            {
                name: "Sonos Amp",
                category: "Architectural Speaker Power",
                icon: "Cpu",
                image: "product_sonos_amp",
                description:
                    "Drive in-wall and in-ceiling speakers throughout the residence with elegant rack-mounted amplification.",
                why: "The rack-mounted amplifier that makes invisible audio possible — zones flow from one set of Sonos Amps in the equipment closet.",
            },
        ],
        benefits: [
            "The most reliable everyday whole-home audio platform — staff and family can operate it without instruction.",
            "Pairs seamlessly with architectural in-wall and in-ceiling speakers for invisible audio in every room.",
            "Hi-Res streaming, native AirPlay, and direct service integration — Apple Music, Tidal, Spotify all just work.",
        ],
        pairsWith: ["home-theater-audio"],
    },
    {
        slug: "bang-olufsen",
        name: "Bang & Olufsen",
        tagline: "Audio as sculpture, sound as theatre.",
        specialty: "Sculptural audio-as-design, statement pieces",
        group: "audio",
        hero: "brand_bang_olufsen_hero",
        intro: [
            "Bang & Olufsen occupies a singular position in the audio market — the only manufacturer whose flagship pieces are designed first as objects and second as instruments. The Beolab 90 is a museum piece; the Beosound Theatre is a piece of furniture; the Beovision Harmony is a sculpture that happens to display 4K HDR.",
            "We specify Bang & Olufsen where the principal wants the equipment to be part of the architectural story — where the speaker is meant to be seen, not concealed.",
        ],
        products: [
            {
                name: "Beosound Theatre",
                category: "All-in-One Cinema Soundbar",
                icon: "Tv",
                image: "product_bo_theatre",
                description:
                    "Twelve channels of Atmos rendered in a sculptural piece of furniture. Designed by Torsten Valeur — engineered as a centerpiece.",
                why: "Pairs naturally with the Beovision Harmony — the only soundbar in the category that earns visibility on its own terms.",
            },
            {
                name: "Beolab 90",
                category: "Flagship 360° Active Loudspeaker",
                icon: "Speaker",
                image: "product_bo_beolab90",
                description:
                    "Eighteen drivers per cabinet, beam-steered acoustics, and a form that belongs in a museum. The most ambitious B&O ever produced.",
                why: "The reference statement speaker — placed in the great room as a piece of art that also reproduces music at audiophile standards.",
            },
            {
                name: "Beosound Balance",
                category: "Vertical Aluminum/Oak Speaker",
                icon: "Speaker",
                image: "product_bo_balance",
                description:
                    "A room-filling wireless speaker that reads as architectural ornament — finished in oak and brushed aluminum.",
                why: "The speaker for the bedroom bench or the entry console — finished as carefully as the millwork around it.",
            },
            {
                name: "Beovision Harmony 88\"",
                category: "OLED on Articulating Wings",
                icon: "Tv",
                image: "product_bo_harmony",
                description:
                    "An 88\" OLED display that folds open like the wings of a butterfly, revealing the screen only when called.",
                why: "The cinematic centerpiece for the great room — closed, it is a sculpture; open, it is the finest OLED on the market.",
            },
        ],
        benefits: [
            "Statement pieces that justify their visibility — these are objects clients want to display, not hide.",
            "Pairs naturally with architectural design — the equipment is the room.",
            "Durability and after-sale support befitting the price — these belong in the residence for decades.",
        ],
        pairsWith: ["home-theater-audio"],
    },
    {
        slug: "sony",
        name: "Sony",
        tagline: "Reference picture, mastered by professionals.",
        specialty: "Display, projection, and reference home cinema",
        group: "display",
        hero: "brand_sony_hero",
        intro: [
            "Sony manufactures the reference displays and projectors used by professional colorists to grade the films you watch. When the project asks for an uncompromising picture — a dedicated cinema, a media room calibrated past the casual — Sony is the only specification.",
            "We commission Sony installations with ISF-certified calibration sessions on-site, so the picture in your residence is measurably as accurate as the master.",
        ],
        products: [
            {
                name: "BRAVIA 9 Mini-LED 4K HDR",
                category: "Reference Display",
                icon: "Tv",
                image: "product_sony_bravia9",
                description:
                    "The flagship LCD reference television — used in professional mastering studios. The benchmark for in-room picture quality.",
                why: "Specified in family rooms and bedrooms where the OLED form factor isn't right — but the picture standard still must be reference.",
            },
            {
                name: "VPL-XW7000ES Native 4K Laser Projector",
                category: "Reference Home Cinema",
                icon: "Film",
                image: "product_sony_projector",
                description:
                    "A native 4K laser projector with 3,200 lumens — built for the dedicated cinema, calibrated by our ISF-certified technicians.",
                why: "The cinema projector we specify when the room is engineered properly — twenty years of laser life without lamp replacement.",
            },
            {
                name: "HT-A9M2 360° Spatial Sound Mapping",
                category: "Wireless Surround System",
                icon: "Speaker",
                image: "product_sony_ht_a9m2",
                description:
                    "Four wireless speakers that map themselves to your room — surround audio without the architectural intrusion.",
                why: "The right answer for the secondary cinema where a full Atmos array would be architecturally disruptive.",
            },
            {
                name: "ES Series A/V Receivers",
                category: "Reference Components",
                icon: "Cpu",
                image: "product_sony_esreceiver",
                description:
                    "Reference-grade preamplification and processing for the dedicated cinema or audiophile listening room.",
                why: "The processing backbone of the dedicated cinema — calibrated as one chain with Sony projection for matched performance.",
            },
        ],
        benefits: [
            "The reference picture quality standard — what colorists use to grade the films you watch.",
            "One brand from display to source to processing — calibrated as an integrated chain.",
            "Long-term firmware support on the ES line — our installs continue to receive updates years out.",
        ],
        pairsWith: ["home-theater-audio"],
    },
    {
        slug: "kaleidescape",
        name: "Kaleidescape",
        tagline: "The reference for the private cinema.",
        specialty: "Reference 4K HDR cinema and movie collection",
        group: "display",
        hero: "brand_kaleidescape_hero",
        intro: [
            "Kaleidescape is the only film source that out-performs commercial cinema — by a meaningful margin. Where streaming services compress, Kaleidescape stores feature films at reference bitrates Blu-ray cannot match.",
            "We specify Kaleidescape in every dedicated cinema, and frequently in the second-tier media rooms. A curated, owned movie library — preserved at reference quality for as long as the residence stands.",
        ],
        products: [
            {
                name: "Strato V",
                category: "Reference 4K HDR Movie Player",
                icon: "Film",
                image: "product_kaleidescape_strato",
                description:
                    "4K/60 HDR10+ playback with bitrates Blu-ray cannot match — the reference for any room calibrated past the casual.",
                why: "The Strato V is the source we trust when the room has been calibrated — every other source is the bottleneck.",
            },
            {
                name: "Terra Prime Server",
                category: "Central Movie Library",
                icon: "Database",
                image: "product_kaleidescape_terra",
                description:
                    "Up to 88 terabytes of locally-stored reference-quality cinema — the master library for distributed playback across the residence.",
                why: "The principal's owned collection — Bondiana, Lean, Hitchcock — held at master quality, available across every cinema in the home.",
            },
            {
                name: "Encore Series",
                category: "Distributed 1U Zone Players",
                icon: "Cpu",
                image: "product_kaleidescape_encore",
                description:
                    "Rack-mounted players that bring the same reference quality to additional cinemas, family rooms, and bedrooms.",
                why: "Reference quality everywhere it is wanted — same masters, same picture, every screen across the residence.",
            },
            {
                name: "Kaleidescape Store",
                category: "Reference-Quality Direct Downloads",
                icon: "Download",
                image: "product_kaleidescape_store",
                description:
                    "Direct downloads of feature films at quality levels not available through any streaming service.",
                why: "New releases purchased the day of theatrical release — at master quality, ready in the cinema by dinner.",
            },
        ],
        benefits: [
            "The only source in the residence that out-performs commercial cinema — by a meaningful margin.",
            "A curated library that ages well — purchased titles remain in the collection forever, in reference quality.",
            "Pairs naturally with calibrated Sony projection, Bang & Olufsen audio, and the dedicated cinema architecture.",
        ],
        pairsWith: ["home-theater-audio"],
    },
    {
        slug: "ubiquiti",
        name: "Ubiquiti",
        tagline: "Modern infrastructure for the technical client.",
        specialty: "Modern unified networking and surveillance",
        group: "networking",
        hero: "brand_ubiquiti_hero",
        intro: [
            "Ubiquiti has built the cleanest unified-infrastructure platform in the category — one mobile app showing every layer of the residence's networking, surveillance, and access control. The platform of choice for the technical principal who wants visibility into their own systems.",
            "We deploy UniFi where the project benefits from consolidation under a single, modern, visually-coherent ecosystem — and where the principal values transparency over abstraction.",
        ],
        products: [
            {
                name: "UniFi Dream Machine Pro Max",
                category: "Gateway / Router / Controller",
                icon: "Cpu",
                image: "product_ubiquiti_udmpromax",
                description:
                    "A single appliance for routing, firewalling, intrusion detection, and network management — built for the technical principal who wants visibility.",
                why: "The whole network on one screen — the principal who wants to look in can; the one who doesn't never has to.",
            },
            {
                name: "UniFi Switch Enterprise",
                category: "Multi-Gig PoE++ Switching",
                icon: "Network",
                image: "product_ubiquiti_switchent",
                description:
                    "Multi-gigabit aggregation with PoE++ — ready for high-density Wi-Fi 7 and 8K AV in the same closet.",
                why: "Future-proofed for the next decade of bandwidth — 8K, Wi-Fi 7, dense PoE all from one switch fabric.",
            },
            {
                name: "UniFi 7 Pro Wall",
                category: "Wi-Fi 7 Wall-Plate AP",
                icon: "Wifi",
                image: "product_ubiquiti_u7prowall",
                description:
                    "A Wi-Fi 7 access point in the form of a standard wall plate — invisible in finished spaces.",
                why: "Wi-Fi 7 coverage that disappears into the wall plate — finished joinery and modern wireless reconciled.",
            },
            {
                name: "UniFi Protect",
                category: "On-Premises Surveillance Platform",
                icon: "Camera",
                image: "product_ubiquiti_protect",
                description:
                    "A complete surveillance ecosystem with on-premises recording — no third-party cloud, no per-camera subscriptions.",
                why: "Surveillance video never leaves the residence — preserves the privacy posture the household requires.",
            },
        ],
        benefits: [
            "The clearest dashboard in the category — clients who care can look in; clients who don't never have to.",
            "Surveillance, networking, and access control unified under one platform — fewer vendors, lower total cost over time.",
            "A single mobile app for the technical principal who wants to see every layer of their residence's infrastructure.",
        ],
        pairsWith: ["networking-wifi", "security-surveillance"],
    },

    // ── Phase 7.2 · 4 new brands ───────────────────────────────────────────────
    {
        slug: "samsung",
        name: "Samsung",
        tagline: "The image, reimagined as architecture.",
        specialty: "Lifestyle displays, reference 8K, and modular MicroLED",
        group: "display",
        hero: "brand_samsung_hero",
        intro: [
            "Samsung's flagship display lineup is the foundation of the contemporary residence — from The Frame's quiet artistic presence to The Wall's seamless modular cinema. Engineered as reference image-makers, finished to belong on the wall as architectural objects.",
            "We specify Samsung in projects where the display has to disappear into the architecture by day and emerge as the centerpiece by night. The integration is meticulous: One Connect Box wiring concealed in millwork, Art Mode gallery curated to the residence, SmartThings logic woven into the wider control fabric.",
        ],
        products: [
            {
                name: "The Frame Pro",
                category: "Lifestyle Art Display",
                icon: "Tv",
                image: "product_samsung_frame_pro",
                description:
                    "A Neo QLED 4K display that lives as a curated artwork on the wall when off, and as a reference television when on. Customizable bezel, motion sensor, and the One Connect Box for an invisible wall installation.",
                why: "The only display in the category that earns its place in the formal living room — at rest, it is the painting your room deserves.",
            },
            {
                name: "Neo QLED 8K",
                category: "Premium 8K Television",
                icon: "Tv",
                image: "product_samsung_neo_qled_8k",
                description:
                    "The reference 8K viewing experience for the principal great room — ultra-thin bezel, NQ8 AI Gen3 processor, and a Mini LED backlight tuned by Samsung's labs.",
                why: "8K is no longer marketing; on a 75-inch+ canvas the picture is closer to the original than any other consumer technology.",
            },
            {
                name: "The Wall MicroLED",
                category: "Modular Cinema Display",
                icon: "Tv",
                image: "product_samsung_the_wall",
                description:
                    "A modular MicroLED display custom-sized to the cinema room — true bezel-less, infinite contrast, and dimensions configured to the room's golden-ratio screen wall.",
                why: "The only display technology with no compromise: no backlight bleed, no viewing angle penalty, no size ceiling.",
            },
            {
                name: "Micro RGB R95H",
                category: "2026 Flagship Color",
                icon: "Tv",
                image: "product_samsung_micro_rgb",
                description:
                    "Samsung's 2026 flagship LCD architecture — Micro RGB backlighting delivers a color volume measurably closer to MicroLED at a fraction of the cost, in sizes from 55 to 115 inches.",
                why: "For the family rooms and bedrooms where The Wall is unjustified, this is the next-best image the year offers.",
            },
        ],
        benefits: [
            "The most refined range of display form factors in the industry — gallery-grade frames, ultra-bezel-less reference panels, and modular MicroLED for the dedicated cinema.",
            "Native SmartThings integration and broad Matter compatibility — the display becomes a sympathetic citizen of the residence.",
            "Mature dealer channel and a multi-year refresh cycle that respects long install timelines.",
        ],
        pairsWith: ["home-theater-audio", "home-automation"],
    },
    {
        slug: "josh-ai",
        name: "Josh.ai",
        tagline: "Conversation, made architectural.",
        specialty: "Local-first AI voice control for the high-end residence",
        group: "control",
        hero: "brand_josh_ai_hero",
        intro: [
            "Josh.ai was built from the ground up for the high-end installation — local processing, owner-and-staff voice recognition, and architectural-grade hardware that defers to the room. Conversation replaces the touch panel, and the home responds.",
            "Where the mass-market voice assistants stop, Josh begins: it speaks the language of Control4, Lutron, Sonos, and the dozens of other platforms already woven into the residence. The result is the rare control surface that does not look like one — because it isn't.",
        ],
        products: [
            {
                name: "Josh Core",
                category: "Whole-Home AI Processor",
                icon: "Cpu",
                image: "product_josh_core",
                description:
                    "The on-premise processor that gives every Josh device its intelligence — natural-language understanding, local command resolution, and the orchestration layer between rooms.",
                why: "Local-first means private. Commands resolved on-site means responsive. The Core is the difference between a voice assistant and a concierge.",
            },
            {
                name: "Josh Nano",
                category: "Architectural Voice Panel",
                icon: "Mic",
                image: "product_josh_nano",
                description:
                    "A flush-mount wall plate with a far-field microphone array — finished to match the architecture, audible in the room, invisible at first glance.",
                why: "The voice control surface that doesn't compromise the elevation drawings. Mount it in every room and the home listens, gracefully.",
            },
            {
                name: "Josh Micro",
                category: "Discreet Voice Microphone",
                icon: "Mic",
                image: "product_josh_micro",
                description:
                    "A compact tabletop or in-ceiling microphone for the rooms where wall plates are not the right answer — in-floor reading rooms, library nooks, walk-in closets.",
                why: "Voice coverage without the visible hardware. Pairs with the distributed audio so the room speaks back through your existing speakers.",
            },
            {
                name: "Josh Edge",
                category: "Adaptive Aluminum Remote",
                icon: "Watch",
                image: "product_josh_edge",
                description:
                    "The new handheld for 2026 — milled aluminum body, near-field voice, capacitive surface, and AI-tuned suggestions that learn the rituals of the house.",
                why: "For the rooms where speaking aloud is the wrong gesture — the formal dining room, the late-evening den — the Edge brings the same intelligence into the hand.",
            },
        ],
        benefits: [
            "The only voice platform purpose-built for luxury residences — not retrofitted from a consumer mass-market product.",
            "Local-first processing keeps commands inside the residence — privacy that doesn't require explanation.",
            "Native integration with Control4, Savant, Lutron, Sonos, and dozens of other platforms already on our shelf.",
        ],
        pairsWith: ["home-automation", "lighting-shades"],
    },
    {
        slug: "coelux",
        name: "CoeLux",
        tagline: "Sunlight, where the architecture forgot.",
        specialty: "Artificial daylight skylights for windowless rooms",
        group: "lighting",
        hero: "brand_coelux_hero",
        intro: [
            "CoeLux is the most rigorous answer in the industry to a problem most luxury residences eventually face — the room that lives below grade, the windowless gallery, the wine cellar, the spa. It does not simulate daylight; it reproduces it.",
            "Using a proprietary nanostructured film engineered with the Italian National Research Council, CoeLux fixtures recreate the optical depth of the sky and the directional beam of the sun. The result is a ceiling that the eye reads as a window, and a room that the principal forgets was ever underground.",
        ],
        products: [
            {
                name: "CoeLux LS Long Sky",
                category: "Modular Linear Skylight",
                icon: "Sun",
                image: "product_coelux_ls",
                description:
                    "The Long Sky family — elongated linear skylights that can be installed alone or chained into the long lines of light a long corridor or gallery deserves. DALI-controllable and available in Matte, Ice, and Array finishes.",
                why: "The long hallway in the basement no longer needs to feel like a hallway in the basement.",
            },
            {
                name: "CoeLux 45 HC",
                category: "Large Ceiling Skylight",
                icon: "SunMedium",
                image: "product_coelux_45_hc",
                description:
                    "A 1.5 m² ceiling-recessed sun-and-sky reproduction with a 45-degree beam — engineered for the larger room: the screening lounge, the principal's gym, the wellness suite.",
                why: "The square that the eye reads as the sky. Not a backlit graphic — the real sky, in the architecture.",
            },
            {
                name: "CoeLux 45 LC",
                category: "Compact Ceiling Skylight",
                icon: "Sun",
                image: "product_coelux_45_lc",
                description:
                    "The 0.5 m² version for medium-height rooms — the same optics, scaled for the wine room, the powder room, the spa.",
                why: "Daylight where engineering said it could not exist. The compact 45 LC fits where the larger fixtures cannot.",
            },
            {
                name: "CoeLux HT 25",
                category: "Vertical Daylight Panel",
                icon: "PanelTop",
                image: "product_coelux_ht25",
                description:
                    "A vertical wall-mounted \"window\" that emits a directional sun-on-blue-sky beam — designed for the rooms where ceilings are constrained and walls are not.",
                why: "The wine cellar with no ceiling height to spare now has a window in the wall — to a sky that is always perfect.",
            },
        ],
        benefits: [
            "The most convincing artificial daylight in the world — nanotechnology developed with the Italian National Research Council to reproduce the optical depth of the actual sky.",
            "A genuine architectural object — not a luminaire pretending to be a window.",
            "Pairs naturally with Lutron and Ketra programming for circadian-rhythm sequencing in spaces no human eye should otherwise forgive.",
        ],
        pairsWith: ["lighting-shades", "home-automation"],
    },
    {
        slug: "k-array",
        name: "K-Array",
        tagline: "Audio that is architecture.",
        specialty: "Aluminum line arrays and architectural sound",
        group: "audio",
        hero: "brand_k_array_hero",
        intro: [
            "K-Array is the Florentine audio house that proved high-end sound systems can be both reference-grade and architecturally invisible. Aluminum-machined to disappear into the structure of the room, engineered to outperform the speakers they replace by a meaningful margin.",
            "Where conventional reference speakers ask the architect to design the room around them, K-Array asks only that the trim detail accommodate them — and then delivers a sound stage that an audiophile will need a moment to reconcile with the visible hardware.",
        ],
        products: [
            {
                name: "Koral KO102",
                category: "Architectural Column Speaker",
                icon: "Speaker",
                image: "product_karray_koral",
                description:
                    "A compact aluminum column with six 3-inch drivers and a high-frequency unit — switchable between pure-array and point-source modes to suit the geometry of the room.",
                why: "The column that performs like a full reference floorstander, in the footprint of a deco picture frame.",
            },
            {
                name: "Anakonda KAN200",
                category: "Flexible Line Array",
                icon: "Speaker",
                image: "product_karray_anakonda",
                description:
                    "A 2-meter-long flexible line array that bends to follow curved walls, hidden coves, or any geometry a traditional speaker cabinet cannot accept.",
                why: "For the rooms whose architecture refused to accommodate sound — the curved gallery, the cove ceiling, the under-stair listening corner.",
            },
            {
                name: "Kobra Series",
                category: "Stainless Line Array",
                icon: "Speaker",
                image: "product_karray_kobra",
                description:
                    "A polished stainless-steel slim line array — 2-inch drivers in a mirror-finish column that reads as architectural hardware, not loudspeaker.",
                why: "When the elevation drawing calls for stainless trim and someone has to deliver music to the room anyway.",
            },
            {
                name: "Tornado Point-Source",
                category: "Compact Aluminum Driver",
                icon: "Speaker",
                image: "product_karray_tornado",
                description:
                    "A 2-inch point-source speaker carved from a single block of anodized aluminum — distributed throughout the residence for invisible coverage.",
                why: "The reference speaker that fits where coverage gaps used to live — the long terrace, the gym mirror wall, the powder room.",
            },
        ],
        benefits: [
            "Slim aluminum forms that integrate as architectural detail — designer-friendly in ways most reference systems are not.",
            "Pure-array engineering delivers consistent coverage without the placement compromises of conventional speakers.",
            "The system specified by clients who want the audio to be both un-seeable and un-mistakable.",
        ],
        pairsWith: ["home-theater-audio"],
    },
];

// Slug → object lookup
export const BRAND_BY_SLUG = Object.fromEntries(BRANDS.map((b) => [b.slug, b]));

// Display-name → slug map for the home page brand wall
export const BRAND_NAME_TO_SLUG = Object.fromEntries(
    BRANDS.map((b) => [b.name, b.slug]),
);
